// src/components/AnimatedNumber.jsx
import React, { useState, useEffect } from "react";

const ANIMATION_DURATION = 1000; // default 1 second

/**
 * AnimatedNumber
 * ──────────────────────────────────────────────────────────────────────
 * Props:
 *   - value: final integer to display (e.g. 4231)
 *   - duration: (optional) how many ms from 0 → value
 *   - delay: (optional) how many ms to wait before starting => count
 *
 * This uses requestAnimationFrame to increment from 0 up to `value` over `duration` ms.
 */
export default function AnimatedNumber({ value, duration = ANIMATION_DURATION, delay = 0 }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    let startTime = null;

    function step(timestamp) {
      if (startTime === null) {
        startTime = timestamp;
      }
      const elapsed = timestamp - startTime;

      // If we haven’t reached `delay`, wait.
      if (elapsed < delay) {
        requestAnimationFrame(step);
        return;
      }

      // After `delay`, update from 0 → value.
      const countElapsed = elapsed - delay;
      const progress = Math.min(countElapsed / duration, 1);
      setDisplay(Math.floor(progress * value));

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        // Ensure we end exactly on `value`.
        setDisplay(value);
      }
    }

    requestAnimationFrame(step);
    // No cleanup necessary for this simple loop
  }, [value, duration, delay]);

  return <span>{display}</span>;
}
